import React, { useState } from 'react';
import {
  Container, Row, Col, Card, Accordion,
  Table, Badge, Button
} from 'react-bootstrap';
import AppHeader from '../../components/Common/AppHeader';
import AppFooter from '../../components/Common/AppFooter';

const standards = [
  {
    title: "UGC / NEP 2020",
    pedagogy: "SLM + OBE",
    regulator: "UGC (India)",
    intent: "NEP 2020 shifts the focus from rote memorization to Outcome-Based Education (OBE). It aligns curriculum, pedagogy, and assessment with clearly defined learning outcomes and graduate attributes, ensuring that education leads to measurable skills and competencies.",
    design: "Structured SLM units with CLO–PLO mapping and modular progression.",
    assessment: "Continuous + summative evaluation with strong self-assessment.",
    structure: "4 Modules × 3 Units | 5500–9000 words",
    link: "https://www.ugc.gov.in"
  },
  {
    title: "SWAYAM MOOC",
    pedagogy: "4-Quadrant MOOC",
    regulator: "Government of India",
    intent: "Scalable digital learning framework for nationwide access.",
    design: "Video + reading + quizzes + discussion forums.",
    assessment: "Weekly quizzes + final exam.",
    structure: "8 Weeks modular format",
    link: "https://swayam.gov.in"
  },
  {
    title: "AICTE Technical",
    pedagogy: "Problem-Based OBE",
    regulator: "AICTE",
    intent: "Industry-ready engineering education.",
    design: "Concept → derivation → numerical → design.",
    assessment: "Numerical-heavy + design problems.",
    structure: "6000–9500 words/unit",
    link: "https://www.aicte-india.org"
  },
  {
    title: "NMC Medical",
    pedagogy: "CBME",
    regulator: "NMC",
    intent: "Clinical competency-focused medical education.",
    design: "Clinical reasoning + diagnosis + treatment.",
    assessment: "Case-based evaluation.",
    structure: "2500–3500 words/unit",
    link: "https://www.nmc.org.in"
  },
  {
    title: "ABET Engineering",
    pedagogy: "Outcome-Based",
    regulator: "ABET (USA)",
    intent: "Global engineering competency standard.",
    design: "Math + design + system thinking.",
    assessment: "Problem-solving + simulations.",
    structure: "6000–9500 words",
    link: "https://www.abet.org"
  },
  {
    title: "AACSB Business",
    pedagogy: "Case-Based",
    regulator: "AACSB",
    intent: "Leadership and strategy education.",
    design: "Case → decision → reflection.",
    assessment: "Case analysis.",
    structure: "5000–8000 words",
    link: "https://www.aacsb.edu"
  },
  {
    title: "QAA UK",
    pedagogy: "Critical Inquiry",
    regulator: "QAA",
    intent: "Academic rigor and quality assurance.",
    design: "Critical reading + discussion.",
    assessment: "Essay-based.",
    structure: "3500–4500 words",
    link: "https://www.qaa.ac.uk"
  },
  {
    title: "BBRRIICCSS",
    pedagogy: "Structured Cognitive Flow",
    regulator: "Texila Framework",
    intent: "Layered instructional design.",
    design: "Background → Bridge → Recall → Relate → Inform → Illustrate → Check → Consolidate",
    assessment: "Continuous reinforcement.",
    structure: "5000–7000 words",
    link: "-"
  },
  {
    title: "Bologna / ECTS",
    pedagogy: "Outcome + Credit",
    regulator: "EU",
    intent: "European credit-based system.",
    design: "Workload aligned learning.",
    assessment: "Credit-based evaluation.",
    structure: "ECTS aligned",
    link: "https://education.ec.europa.eu"
  },
  {
    title: "CAHIIM",
    pedagogy: "Competency-Based",
    regulator: "CAHIIM",
    intent: "Health informatics education.",
    design: "Data + healthcare integration.",
    assessment: "Case + analytics tasks.",
    structure: "2500–3500 words",
    link: "https://www.cahiim.org"
  },
  {
    title: "DEAC",
    pedagogy: "Online Learning",
    regulator: "DEAC",
    intent: "Distance education quality.",
    design: "Discussion + reading.",
    assessment: "Interactive participation.",
    structure: "Online modules",
    link: "https://www.deac.org"
  },
  {
    title: "CDIO",
    pedagogy: "Engineering Lifecycle",
    regulator: "CDIO",
    intent: "Real-world engineering lifecycle training.",
    design: "Design → implement → operate.",
    assessment: "Project-based.",
    structure: "6000–9500 words",
    link: "https://www.cdio.org"
  },
  {
    title: "WFME",
    pedagogy: "Medical Competency",
    regulator: "WFME",
    intent: "Global medical standards.",
    design: "Clinical case learning.",
    assessment: "Patient-based evaluation.",
    structure: "2500–3500 words",
    link: "https://wfme.org"
  },
  {
    title: "ACEN",
    pedagogy: "Nursing Competency",
    regulator: "ACEN",
    intent: "Nursing education quality.",
    design: "Care plan + patient case.",
    assessment: "Clinical reflection.",
    structure: "2500–3500 words",
    link: "https://www.acenursing.org"
  },
  {
    title: "EFMD / EQUIS",
    pedagogy: "Business Strategy",
    regulator: "EFMD",
    intent: "European business education.",
    design: "Case + strategy.",
    assessment: "Decision analysis.",
    structure: "5000–8000 words",
    link: "https://efmdglobal.org"
  },
  {
    title: "EQF",
    pedagogy: "Outcome-Based",
    regulator: "European Commission",
    intent: "Qualification alignment across Europe.",
    design: "Learning outcomes.",
    assessment: "Competency validation.",
    structure: "Level-based",
    link: "https://europa.eu"
  }
];

const ContentStandards = () => {
  const [selected, setSelected] = useState([]);

  const toggleSelect = (std) => {
    if (selected.includes(std)) {
      setSelected(selected.filter(s => s !== std));
    } else {
      setSelected([...selected, std]);
    }
  };

  return (
    <div className="bg-light min-vh-100 d-flex flex-column">
      

      <Container className="py-5 flex-grow-1">
        <div className="text-center mb-5">
          <Badge bg="primary">AcademicOS Compliance Layer</Badge>
          <h1 className="fw-bold mt-2">Global Content Standards Framework</h1>
          <p className="text-muted">
            Structured academic frameworks governing curriculum design, pedagogy, and assessment integrity.
          </p>
        </div>

        {/* Accordion */}
        <Accordion>
          {standards.map((std, idx) => (
            <Accordion.Item eventKey={idx.toString()} key={idx}>
              <Accordion.Header>
                <div className="d-flex justify-content-between w-100">
                  <span>{std.title}</span>
                  <Button
                    size="sm"
                    variant={selected.includes(std) ? "danger" : "outline-primary"}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleSelect(std);
                    }}
                  >
                    {selected.includes(std) ? "Remove" : "Compare"}
                  </Button>
                </div>
              </Accordion.Header>

              <Accordion.Body>
                <Row>
                  <Col md={6}>
                    <h6 className="fw-bold">Academic Intent</h6>
                    <p>{std.intent}</p>

                    <h6 className="fw-bold">Pedagogical Model</h6>
                    <p>{std.pedagogy}</p>
                  </Col>

                  <Col md={6}>
                    <h6 className="fw-bold">Instructional Design</h6>
                    <p>{std.design}</p>

                    <h6 className="fw-bold">Assessment Philosophy</h6>
                    <p>{std.assessment}</p>
                  </Col>
                </Row>

                <hr />

                <h6 className="fw-bold">Structural Configuration</h6>
                <p>{std.structure}</p>

                {/* Regulatory */}
                <Card className="mt-3 bg-light border-0">
                  <Card.Body>
                    <h6 className="fw-bold">Regulatory Traceability</h6>
                    <p><strong>Authority:</strong> {std.regulator}</p>
                    <p><strong>Source:</strong> {std.link}</p>
                  </Card.Body>
                </Card>
              </Accordion.Body>
            </Accordion.Item>
          ))}
        </Accordion>

        {/* Comparison Mode */}
        {selected.length >= 2 && (
          <Card className="mt-5 shadow-sm">
            <Card.Header className="bg-dark text-white">
              Standard Comparison Mode
            </Card.Header>
            <Card.Body>
              <Table bordered>
                <thead>
                  <tr>
                    <th>Parameter</th>
                    {selected.map((s, i) => <th scope="col" key={i}>{s.title}</th>)}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Pedagogy</td>
                    {selected.map((s, i) => <td key={i}>{s.pedagogy}</td>)}
                  </tr>
                  <tr>
                    <td>Assessment</td>
                    {selected.map((s, i) => <td key={i}>{s.assessment}</td>)}
                  </tr>
                  <tr>
                    <td>Regulator</td>
                    {selected.map((s, i) => <td key={i}>{s.regulator}</td>)}
                  </tr>
                  <tr>
                    <td>Structure</td>
                    {selected.map((s, i) => <td key={i}>{s.structure}</td>)}
                  </tr>
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        )}
      </Container>

      <AppFooter />
    </div>
  );
};

export default ContentStandards;
